import { Camera, Check, KeyRound, Save, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { SkeletonBlock } from "@/components/ui/Skeleton";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { getSelfRetailerId, getSession, setSession } from "@/components/feature/auth/session";
import { retailerService } from "@/services/apiServices";
import type { RetailerProfile } from "@/types/wallet";
import { formatDate, formatTime } from "@/pages/retailer/wallet/data";

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  retailer_onboarding_executive: "Retailer Onboarding Executive",
  retailer_onboarding_executive_walletrequesthandler: "Retailer Onboarding Executive & Wallet Request Handler",
  wallet_request_handler: "Wallet Request Handler",
};

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function ProfilePage() {
  return (
    <ToastProvider>
      <ProfileInner />
    </ToastProvider>
  );
}

function ProfileInner() {
  const toast = useToast();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const session = getSession();
  const retailerId = getSelfRetailerId();
  const effectiveUserId = (isAdmin ? session?.id : retailerId) || session?.id || retailerId || "";

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<RetailerProfile | null>(null);
  const [kycStatus, setKycStatus] = useState<string>("NOT_STARTED");

  const [details, setDetails] = useState({
    name: "",
    email: "",
    shopName: "",
    city: "",
    state: "",
    fullAddress: "",
    gst: "",
  });
  const [savingDetails, setSavingDetails] = useState(false);

  const [pw, setPw] = useState({ current: "", newPw: "", confirm: "" });
  const [updatingPw, setUpdatingPw] = useState(false);
  const pwRef = useRef<HTMLDivElement>(null);

  const [mpin, setMpin] = useState({ current: "", newMpin: "", confirm: "" });
  const [updatingMpin, setUpdatingMpin] = useState(false);

  const load = useCallback(async () => {
    if (!effectiveUserId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await retailerService.getProfile(effectiveUserId);
      const p: RetailerProfile | undefined = res?.data?.profile;
      if (p) {
        setProfile(p);
        setDetails({
          name: p.name ?? session?.name ?? "",
          email: p.email ?? session?.email ?? "",
          shopName: p.shopName ?? "",
          city: p.city ?? "",
          state: p.state ?? "",
          fullAddress: p.fullAddress ?? "",
          gst: p.gst ?? "",
        });
        if (p.mpin) {
          setMpin((prev) => ({ ...prev, current: p.mpin || "" }));
        }
      }
      setKycStatus(res?.data?.kyc?.status ?? p?.kycStatus ?? "NOT_STARTED");
    } catch (err: any) {
      toast.error(err?.message || "Failed to load your profile");
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, session?.name, session?.email, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (location.search.includes("section=password")) {
      const t = setTimeout(() => {
        pwRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [location.search]);

  const checks = [
    { label: "At least 8 characters", ok: pw.newPw.length >= 8 },
    { label: "Contains an uppercase letter", ok: /[A-Z]/.test(pw.newPw) },
    { label: "Contains a number", ok: /\d/.test(pw.newPw) },
    { label: "Contains a special character", ok: /[^A-Za-z0-9]/.test(pw.newPw) },
  ];
  const strength = checks.filter((c) => c.ok).length;

  const kycLocked = kycStatus === "APPROVED" || kycStatus === "UNDER_REVIEW";

  async function saveDetails() {
    if (!details.name.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    setSavingDetails(true);
    try {
      const res = await retailerService.updateProfile(effectiveUserId, {
        name: details.name,
        email: details.email,
        shopName: details.shopName,
        city: details.city,
        state: details.state,
        fullAddress: details.fullAddress,
        gst: details.gst,
      });
      if (res?.data?.profile) {
        setProfile(res.data.profile);
        const cur = getSession();
        if (cur) {
          setSession({
            ...cur,
            name: res.data.profile.name || details.name,
            email: res.data.profile.email || details.email,
          });
        }
      }
      toast.success(res?.message || "Account details saved");
    } catch (err: any) {
      toast.error(err?.message || "Could not save your details");
    } finally {
      setSavingDetails(false);
    }
  }

  async function updatePassword() {
    if (!pw.current || !pw.newPw || !pw.confirm) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (pw.newPw !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (pw.newPw.length < 8) {
      toast.error("Your new password must be at least 8 characters");
      return;
    }
    setUpdatingPw(true);
    try {
      const res = await retailerService.changePassword(effectiveUserId, {
        currentPassword: pw.current,
        newPassword: pw.newPw,
      });
      setPw({ current: "", newPw: "", confirm: "" });
      toast.success(res?.message || "Password updated successfully");
    } catch (err: any) {
      toast.error(err?.message || "Could not update your password");
    } finally {
      setUpdatingPw(false);
    }
  }

  async function updateMpin() {
    if (!mpin.current || !mpin.newMpin || !mpin.confirm) {
      toast.error("Please fill in all MPIN fields");
      return;
    }
    if (!/^\d{4}$/.test(mpin.newMpin)) {
      toast.error("Your new MPIN must be exactly 4 digits");
      return;
    }
    if (mpin.newMpin !== mpin.confirm) {
      toast.error("The new MPINs do not match");
      return;
    }
    setUpdatingMpin(true);
    try {
      const res = await retailerService.changeMpin(retailerId, {
        currentMpin: mpin.current,
        newMpin: mpin.newMpin,
      });
      const updatedMpin = mpin.newMpin;
      setProfile((prev) => (prev ? { ...prev, mpin: updatedMpin, hasMpin: true } : prev));
      setMpin({ current: updatedMpin, newMpin: "", confirm: "" });
      toast.success(res?.message || "MPIN updated successfully");
    } catch (err: any) {
      toast.error(err?.message || "Could not update your MPIN");
    } finally {
      setUpdatingMpin(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
        <SkeletonBlock className="h-8 w-48" />
        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[35%_65%]">
          <SkeletonBlock className="h-72 rounded-[10px]" />
          <div className="flex flex-col gap-5">
            <SkeletonBlock className="h-80 rounded-[10px]" />
            <SkeletonBlock className="h-80 rounded-[10px]" />
          </div>
        </div>
      </div>
    );
  }

  const roleKey = profile?.role || session?.role || "staff";
  const roleLabel = ROLE_DISPLAY_NAMES[roleKey] || (isAdmin ? "Staff User" : "Retailer");
  const displayName = details.name || profile?.name || session?.name || (isAdmin ? "Staff User" : "Retailer");

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">My Profile</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          {isAdmin
            ? "Manage your staff account credentials and personal information"
            : "Manage your account details, password and MPIN"}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[35%_65%]">
        <div className="h-fit rounded-[10px] border border-line bg-card p-6 shadow-card">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary-50 text-[32px] font-semibold text-primary-500">
              {initialsOf(displayName)}
            </div>
            <h2 className="mt-4 text-lg font-semibold text-ink">{displayName}</h2>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              {isAdmin ? roleLabel : (profile?.shopName || "—")}
            </p>
            <div className="mt-4">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Camera className="h-4 w-4" />}
                onClick={() => toast.info("Profile photos aren't stored yet")}
              >
                Change Photo
              </Button>
            </div>
          </div>

          <div className="mt-6 divide-y divide-line border-t border-line pt-1">
            <div className="flex items-center justify-between gap-3 py-3">
              <span className="text-[13px] text-ink-secondary">{isAdmin ? "Staff ID" : "Retailer ID"}</span>
              <span className="font-mono text-sm font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                {profile?.id || session?.id || "—"}
              </span>
            </div>

            {isAdmin ? (
              <div className="flex items-center justify-between gap-3 py-3">
                <span className="text-[13px] text-ink-secondary">Assigned Role</span>
                <span className="text-sm font-medium text-ink capitalize">{roleLabel}</span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 py-3">
                  <span className="text-[13px] text-ink-secondary">KYC Status</span>
                  <span className="text-sm font-medium text-ink">{kycStatus.replace(/_/g, " ")}</span>
                </div>
                <div className="flex items-center justify-between gap-3 py-3">
                  <span className="text-[13px] text-ink-secondary">Wallet Balance</span>
                  <span className="text-sm font-medium text-ink">
                    ₹{(profile?.walletBalance ?? 0).toLocaleString("en-IN")}
                  </span>
                </div>
              </>
            )}

            <div className="flex items-center justify-between gap-3 py-3">
              <span className="text-[13px] text-ink-secondary">Member Since</span>
              <span className="text-sm font-medium text-ink">
                {profile?.joinedAt ? formatDate(profile.joinedAt) : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 py-3">
              <span className="text-[13px] text-ink-secondary">Last Login</span>
              <span className="text-right text-sm font-medium text-ink">
                {profile?.lastLoginAt
                  ? `${formatDate(profile.lastLoginAt)}, ${formatTime(profile.lastLoginAt)}`
                  : "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
            <h3 className="text-lg font-semibold text-ink">Account Details</h3>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              {isAdmin ? "Your personal contact credentials" : "Your personal and business information"}
            </p>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Full Name"
                value={details.name}
                onChange={(e) => setDetails({ ...details, name: e.target.value })}
              />
              <Input
                label="Mobile Number"
                value={profile?.mobile ?? ""}
                readOnly
                className="bg-[#F8FAFC] text-ink-secondary"
                helper="Contact administrator to change registered mobile number"
              />
              <div className="sm:col-span-2">
                <Input
                  label="Email Address"
                  type="email"
                  value={details.email}
                  onChange={(e) => setDetails({ ...details, email: e.target.value })}
                />
              </div>

              {isAdmin && (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-ink">Assigned Role</label>
                  <div className="flex items-center gap-2 rounded-md border border-line bg-slate-50 px-3 py-2 text-sm text-ink-secondary">
                    <ShieldCheck className="h-4 w-4 text-primary-500" />
                    <span className="font-medium text-ink">{roleLabel}</span>
                  </div>
                </div>
              )}

              {!isAdmin && (
                <>
                  <div className="sm:col-span-2">
                    <Input
                      label="Shop/Business Name"
                      value={details.shopName}
                      onChange={(e) => setDetails({ ...details, shopName: e.target.value })}
                    />
                  </div>
                  <Input
                    label="City"
                    value={details.city}
                    onChange={(e) => setDetails({ ...details, city: e.target.value })}
                  />
                  <Input
                    label="State"
                    value={details.state}
                    onChange={(e) => setDetails({ ...details, state: e.target.value })}
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="Business Address"
                      value={details.fullAddress}
                      onChange={(e) => setDetails({ ...details, fullAddress: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Input
                      label="GST Number"
                      value={details.gst}
                      onChange={(e) => setDetails({ ...details, gst: e.target.value.toUpperCase() })}
                      readOnly={kycLocked}
                      className={kycLocked ? "bg-[#F8FAFC] text-ink-secondary" : ""}
                      helper={
                        kycLocked
                          ? "Business details are locked once your KYC has been submitted"
                          : "15 characters, optional"
                      }
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <Button onClick={saveDetails} loading={savingDetails} leftIcon={<Save className="h-4 w-4" />}>
                Save Changes
              </Button>
            </div>
          </div>

          <div
            ref={pwRef}
            id="change-password"
            className="scroll-mt-24 rounded-[10px] border border-line bg-card p-5 shadow-card"
          >
            <h3 className="text-lg font-semibold text-ink">Change Password</h3>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              Update the password used to sign in to your account
            </p>

            <div className="mt-5 flex flex-col gap-4">
              <Input
                label="Current Password"
                type="password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
              />
              <div>
                <Input
                  label="New Password"
                  type="password"
                  value={pw.newPw}
                  onChange={(e) => setPw({ ...pw, newPw: e.target.value })}
                />
                <div className="mt-2 flex items-center gap-1">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        i < strength ? (strength <= 2 ? "bg-warning" : "bg-success") : "bg-slate-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="mt-2.5 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {checks.map((c) => (
                    <div key={c.label} className="flex items-center gap-1.5 text-xs">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded-full ${
                          c.ok ? "bg-success-bg text-success" : "bg-slate-100 text-ink-muted"
                        }`}
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className={c.ok ? "text-ink" : "text-ink-secondary"}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Input
                label="Confirm New Password"
                type="password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                error={pw.confirm && pw.confirm !== pw.newPw ? "Passwords do not match" : undefined}
              />
            </div>

            <div className="mt-5 flex justify-end">
              <Button onClick={updatePassword} loading={updatingPw} leftIcon={<KeyRound className="h-4 w-4" />}>
                Update Password
              </Button>
            </div>
          </div>

          {!isAdmin && (
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <h3 className="text-lg font-semibold text-ink">Transaction MPIN</h3>
              <p className="mt-0.5 text-[13px] text-ink-secondary">
                The 4-digit PIN you enter when creating a mandate
              </p>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Current MPIN"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={mpin.current}
                  onChange={(e) => setMpin({ ...mpin, current: e.target.value.replace(/\D/g, "") })}
                />
                <Input
                  label="New MPIN"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={mpin.newMpin}
                  onChange={(e) => setMpin({ ...mpin, newMpin: e.target.value.replace(/\D/g, "") })}
                />
                <Input
                  label="Confirm New MPIN"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={mpin.confirm}
                  onChange={(e) => setMpin({ ...mpin, confirm: e.target.value.replace(/\D/g, "") })}
                  error={mpin.confirm && mpin.confirm !== mpin.newMpin ? "MPINs do not match" : undefined}
                />
              </div>

              <div className="mt-5 flex justify-end">
                <Button
                  onClick={updateMpin}
                  loading={updatingMpin}
                  leftIcon={<ShieldCheck className="h-4 w-4" />}
                >
                  Update MPIN
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
